import type { TechnicalSupportModalOption } from "../Products";
import type { FormTCRq } from "./validationSchema";
import type { ChangeEvent } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Modal } from "@mui/material";
import { Button } from "@components/index";
import { SecureSubmitButton } from "@components/buttons/SecureSubmitButton";
import { useStore } from "@stores/RootStore";
import { colors } from "@theme/colorsCustomer";
import { fonts } from "@theme/fontsCustomer";
import { BulletIcon } from "@icons/BulletIcon";
import { useAddTechnicalInquiry } from "@queryHooks/useTechnicalSupport";
import { useTCForm } from "./validationSchema";

type TechnicalSupportCreateModalProps = {
  modalOption: TechnicalSupportModalOption;
  onClose: () => void;
};

export const TechnicalSupportCreateModal = ({
  modalOption,
  onClose,
}: TechnicalSupportCreateModalProps) => {
  const { t } = useTranslation();
  const { isOpen, subscriptionId, tenantId } = modalOption;

  const { mutate: addTechnicalInquiry, isPending } = useAddTechnicalInquiry();

  const { control, watch, handleSubmit } = useTCForm();
  const {
    uiStore: { toastStore },
  } = useStore();

  const inquiry = watch("inquiry");

  const validation = {
    success: (data: FormTCRq) => {
      const createInquiryData = {
        inquiry: data.inquiry,
        subscriptionId,
        tenantId,
      };
      addTechnicalInquiry(createInquiryData, {
        onSuccess: () => {
          toastStore.open({
            message: t("Technical_Support.Inquiry_Success"),
            hideAction: true,
          });
          onClose();
        },
        onError: () => {
          toastStore.open({
            message: t("Technical_Support.Inquiry_Failed"),
            hideAction: true,
          });
          onClose();
        },
      });
    },
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Container onSubmit={handleSubmit(validation.success)}>
        <StyledModalHeader>{t("Technical_Support.Title")}</StyledModalHeader>
        <StyledModalBody>
          <Description>
            <DescriptionList>
              <BulletIcon color={colors.light["ic-gray-lighter"]} />{" "}
              {t("Technical_Support.Contact_Notice")}
            </DescriptionList>
            <DescriptionList>
              <BulletIcon color={colors.light["ic-gray-lighter"]} />{" "}
              {t("Technical_Support.Admin_Notice")}
            </DescriptionList>
          </Description>
          <InquiryTitle>
            <span className="inquiry">
              {t("Technical_Support.Inquiry_Content")}
              <span className="mark">*</span>
            </span>
            <span className="inquiry-count">({inquiry.length}/300)</span>
          </InquiryTitle>
          <Controller
            control={control}
            name="inquiry"
            render={({ field: { onChange, value } }) => (
              <InquiryTextArea
                placeholder={t("Technical_Support.Inquiry_Placeholder")}
                value={value}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                  if (e.target.value.length < 301) onChange(e);
                }}
              />
            )}
          />
        </StyledModalBody>
        <StyledModalFooter>
          <StyledSecureFooterButton
            isPending={isPending}
            variant="contain"
            type="submit"
            size="extraLarge"
            fullWidth
          >
            {t("Technical_Support.Complete")}
          </StyledSecureFooterButton>
          <StyledFooterButton
            variant="outline"
            size="extraLarge"
            fullWidth
            onClick={onClose}
          >
            {t("Technical_Support.Cancel")}
          </StyledFooterButton>
        </StyledModalFooter>
      </Container>
    </Modal>
  );
};

const Container = styled.form`
  position: absolute;
  top: 50%;
  left: 58%;
  transform: translate(-50%, -50%);
  width: 440px;
  background-color: ${({ theme }) => theme.colors["bg-white"]};
  border: 2px solid #fff;
  border-radius: 6px;
  padding: 0px 28px;
`;

const StyledModalHeader = styled.article`
  ${fonts.Headline6}
  color: ${({ theme }) => theme.colors["text-gray-main"]};
  padding: 18px 0 16px;
`;

const StyledModalBody = styled.article`
  height: 440px;
`;

const Description = styled.div`
  width: 100%;
  border-radius: 3px;
  background: ${({ theme }) => theme.colors["bg-gray-light"]};
  padding: 12px 0 12px 8px;
  margin-top: 4px;
`;

const DescriptionList = styled.div`
  ${fonts.Body4}
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors["text-gray-sub-dark"]};
`;

const InquiryTitle = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
  .inquiry {
    ${fonts.Headline8}
    color: ${({ theme }) => theme.colors["text-gray-main"]};
    .mark {
      color: ${({ theme }) => theme.colors["text-red-light"]};
    }
  }
  .inquiry-count {
    ${fonts.Body4}
    color: ${({ theme }) => theme.colors["text-gray-light"]};
  }
`;

const InquiryTextArea = styled.textarea`
  width: 100%;
  height: 200px;
  padding: 10px 12px;
  resize: none;
  margin-top: 4px;
  border: 1px solid ${({ theme }) => theme.colors["border-gray-lighter"]};
  border-radius: 2px;
`;

const StyledModalFooter = styled.article`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 8px;
  padding: 24px 0px 28px;
`;

const StyledFooterButton = styled(Button)`
  flex-shrink: 1;
`;

const StyledSecureFooterButton = styled(SecureSubmitButton)`
  flex-shrink: 1;
`;
